/**
 * Utility function to handle profile image URLs consistently across the application.
 * It handles absolute URLs, relative URLs, and blob URLs.
 * 
 * @param path The profile image path or URL from the backend
 * @returns A full URL that can be used in an <img> tag's src attribute
 */
export const getProfileImageUrl = (path: string | null | undefined): string => {
    if (!path) {
        // Return a default mystery person avatar if no path is provided
        return 'https://encrypted-tbn0.gstatic.com/images?q=tbn:ANd9GcRM0nI6o9l7fyionBcUIlUC15MjBMqIOIcmTQ&s';
    }

    // If it's already a full URL or a blob URL, return it as is
    if (path.startsWith('http') || path.startsWith('blob:') || path.startsWith('data:')) {
        return path;
    }

    // Ensure path starts with a slash for consistent joining
    const normalizedPath = path.startsWith('/') ? path : `/${path}`;

    // Prefer API base origin (same backend serves /uploads), then WS URL.
    const apiUrl = String(process.env.REACT_APP_API_URL || '').trim();
    let baseUrl = '';
    if (apiUrl) {
        baseUrl = apiUrl.replace(/\/api\/?$/i, '');
    } else {
        baseUrl = String(process.env.REACT_APP_WS_URL || '').trim();
    }
    if (!baseUrl) baseUrl = 'http://localhost:8001';

    // Ensure baseUrl has a protocol
    if (!baseUrl.startsWith('http')) {
        baseUrl = `http://${baseUrl}`;
    }

    // Combine base URL and path, ensuring no double slashes
    const cleanBaseUrl = baseUrl.replace(/\/$/, '');
    return `${cleanBaseUrl}${normalizedPath}`;
};
/**
 * Fallback image URL for when the primary profile image fails to load.
 */
export const DEFAULT_AVATAR_URL = 'https://encrypted-tbn0.gstatic.com/images?q=tbn:ANd9GcRM0nI6o9l7fyionBcUIlUC15MjBMqIOIcmTQ&s';

